import http from '../../api/http-client';
import axios from 'axios';
import appConfig from '../../config/app.config';
import { clearAccessToken, setAccessToken } from './token-store';

export interface User {
  _id: string;
  username: string;
  name?: string;
  email?: string;
  role: 'user' | 'admin';
  avatarUrl?: string;
  emailVerified: boolean;
  online?: boolean;
  lastLoggedInAt?: string;
}

export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challenge: string;
}

export function isTwoFactorChallenge(value: User | TwoFactorChallenge): value is TwoFactorChallenge {
  return (value as TwoFactorChallenge).twoFactorRequired === true;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface ActiveSession {
  _id: string;
  userAgent?: string;
  ip?: string;
  lastUsedAt?: string;
  createdAt: string;
  /** The device asking. Marked so nobody signs themselves out by mistake. */
  current: boolean;
}

export interface SignInMethods {
  hasPassword: boolean;
  providers: ('google' | 'facebook')[];
  providerAvatarUrl: string | null;
}

export interface ProfileChanges {
  name?: string;
  /** null means "show my initials instead". */
  avatarUrl?: string | null;
}

interface SessionResponse {
  accessToken: string;
  user: User;
}

class AuthService {
  /**
   * With two-step sign-in on, the password buys a challenge rather than a
   * session — so this can return "not finished yet" instead of a user, and
   * every caller has to deal with that.
   */
  async login(credentials: LoginCredentials): Promise<User | TwoFactorChallenge> {
    const { data } = await http.post<SessionResponse & TwoFactorChallenge>(
      '/auth/login',
      credentials
    );

    if (data.twoFactorRequired) return { twoFactorRequired: true, challenge: data.challenge };

    setAccessToken(data.accessToken);
    return data.user;
  }

  /** Second step: the code from the app, or one recovery code. */
  async completeTwoFactor(challenge: string, code: string): Promise<User> {
    const { data } = await http.post<SessionResponse>('/auth/2fa/verify', { challenge, code });
    setAccessToken(data.accessToken);
    return data.user;
  }

  async register(input: { username: string; email: string; password: string }): Promise<User> {
    const { data } = await http.post<SessionResponse>('/auth/register', input);
    setAccessToken(data.accessToken);
    return data.user;
  }

  /**
   * Restore a session on page load from the refresh cookie.
   *
   * Uses a bare axios call so the shared client's 401 interceptor does not
   * treat a signed-out visitor as an expired session and bounce them to login.
   */
  async bootstrap(): Promise<User | null> {
    try {
      const { data } = await axios.post<SessionResponse>(
        `${appConfig.apiBaseUrl}/auth/refresh-token`,
        {},
        { withCredentials: true }
      );
      setAccessToken(data.accessToken);
      return data.user;
    } catch {
      clearAccessToken();
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await http.post('/auth/logout');
    } catch {
      // The server may already have ended the session; clearing locally is
      // still correct.
    } finally {
      clearAccessToken();
    }
  }

  async logoutEverywhere(): Promise<void> {
    try {
      await http.post('/auth/logout-all');
    } finally {
      clearAccessToken();
    }
  }

  async listSessions(): Promise<ActiveSession[]> {
    const { data } = await http.get<{ sessions: ActiveSession[] }>('/auth/sessions');
    return data.sessions;
  }

  /** End one other device without touching this one. */
  async revokeSession(sessionId: string): Promise<void> {
    await http.delete(`/auth/sessions/${sessionId}`);
  }

  // ── Profile ─────────────────────────────────────────────────────────────

  /** What the settings page needs to know before deciding what to offer. */
  async signInMethods(): Promise<SignInMethods> {
    const { data } = await http.get<SignInMethods>('/auth/methods');
    return data;
  }

  /** Send only what changed — the server treats an absent field as "leave it". */
  async updateProfile(changes: ProfileChanges): Promise<User> {
    const { data } = await http.patch<{ user: User }>('/auth/profile', changes);
    return data.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<string> {
    const { data } = await http.post<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return data.message;
  }

  // ── Email verification ──────────────────────────────────────────────────
  async verifyEmail(token: string): Promise<User> {
    const { data } = await http.post<{ user: User }>('/auth/verify-email', { token });
    return data.user;
  }

  async resendVerification(email: string): Promise<void> {
    await http.post('/auth/verify-email/resend', { email });
  }

  // ── Password reset ──────────────────────────────────────────────────────
  async forgotPassword(email: string): Promise<void> {
    await http.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string): Promise<User> {
    const { data } = await http.post<SessionResponse>('/auth/reset-password', { token, password });
    setAccessToken(data.accessToken);
    return data.user;
  }

  /** Which social providers are configured on the server. */
  async availableProviders(): Promise<string[]> {
    try {
      const { data } = await http.get<{ providers: string[] }>('/auth/providers');
      return data.providers ?? [];
    } catch {
      return [];
    }
  }
}

const authService = new AuthService();
export default authService;
